/**
 * Auswahlmodus der Mailliste auf dem Handy (wie Outlook iOS): langer Druck auf
 * eine Mail wählt sie aus, weiteres Antippen wählt mit ab/an, „Alle auswählen"
 * nimmt die sichtbaren Zeilen. Bewusst ohne React, damit die Regeln ohne DOM
 * prüfbar bleiben.
 *
 * Schlüssel ist die Message-Id. Jede ausgewählte Zeile merkt sich ihr
 * Postfach — Suchtreffer können aus verschiedenen Postfächern stammen, und
 * jede Aktion läuft gegen das Postfach der jeweiligen Mail.
 */

export interface AuswahlEintrag<E extends { id: string; read: boolean } = { id: string; read: boolean }> {
    email: E;
    mailbox: string;
}

export type MailAuswahl<E extends { id: string; read: boolean } = { id: string; read: boolean }> = ReadonlyMap<
    string,
    AuswahlEintrag<E>
>;

/** Nimmt die Zeile in die Auswahl auf bzw. wieder heraus (neue Map). */
export function schalteAuswahl<E extends { id: string; read: boolean }>(
    auswahl: MailAuswahl<E>,
    eintrag: AuswahlEintrag<E>,
): MailAuswahl<E> {
    const next = new Map(auswahl);
    if (next.has(eintrag.email.id)) next.delete(eintrag.email.id);
    else next.set(eintrag.email.id, eintrag);
    return next;
}

/** „Alle auswählen": genau die übergebenen (sichtbaren) Zeilen. */
export function alleAuswaehlen<E extends { id: string; read: boolean }>(
    eintraege: readonly AuswahlEintrag<E>[],
): MailAuswahl<E> {
    return new Map(eintraege.map((e) => [e.email.id, e]));
}

/** Stehen alle sichtbaren Zeilen schon in der Auswahl? (Leere Liste → nein.) */
export function sindAlleAusgewaehlt(
    auswahl: MailAuswahl,
    eintraege: readonly { email: { id: string } }[],
): boolean {
    return eintraege.length > 0 && eintraege.every((e) => auswahl.has(e.email.id));
}

/**
 * Was der Lese-Knopf der Aktionsleiste tut: ist auch nur EINE ausgewählte Mail
 * ungelesen, markiert er alle als gelesen — sonst alle als ungelesen (wie
 * Outlook). `true` = als gelesen markieren.
 */
export function zielLesestatus(auswahl: MailAuswahl): boolean {
    for (const e of auswahl.values()) if (!e.email.read) return true;
    return false;
}

/**
 * Das gemeinsame Postfach aller ausgewählten Mails, sonst `null`. Verschieben
 * in einen Ordner geht nur innerhalb EINES Postfachs — die Ordner-Ids sind je
 * Postfach eigene.
 */
export function gemeinsamesPostfach(auswahl: MailAuswahl): string | null {
    let mb: string | null = null;
    for (const e of auswahl.values()) {
        const m = e.mailbox.toLowerCase();
        if (mb === null) mb = m;
        else if (mb !== m) return null;
    }
    if (mb === null) return null;
    // Originalschreibweise der ersten Mail zurückgeben.
    return auswahl.values().next().value?.mailbox ?? null;
}

/** Kopfzeile im Auswahlmodus: „1 ausgewählt", „3 ausgewählt". */
export function auswahlTitel(anzahl: number): string {
    return `${anzahl} ausgewählt`;
}
